import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Account, MonthlyAccount, MonthlyAccounts } from '../model/accounting.model';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { Scenario } from '../store/scenario.model';
import { incrementYear } from '../model/period.model';
import { LocalStorageService } from '../../home/services/local-storage.service';

export const CSV_SEPARATOR = ';';

@Injectable({
  providedIn: 'root'
})
export class ScenarioService {

  constructor(
    private functions: AngularFireFunctions,
    private localStorageService: LocalStorageService
  ) {
  }

  getScenarios = (companyId: string): Observable<Scenario[]> => {
    const callable = this.functions.httpsCallable('listScenarios');
    return callable({
      companyId,
      accessToken: this.localStorageService.getAccessToken()
    });
  };

  saveScenarioValue = (companyId: string, monthlyAccount: MonthlyAccount | MonthlyAccount[], scenarioId: string | undefined): Observable<string> => {
    const callable = this.functions.httpsCallable('updateScenario');
    return callable({
      companyId,
      monthlyAccount,
      scenarioId,
      accessToken: this.localStorageService.getAccessToken()
    });
  };

  getScenarioValues = (companyId: string, scenarioId: string): Observable<any> => {
    const callable = this.functions.httpsCallable('getScenario');
    return callable({
      companyId,
      scenarioId,
      accessToken: this.localStorageService.getAccessToken()
    });
  };

  createScenario = (companyId: string, scenarioName: string): Observable<Scenario> => {
    const callable = this.functions.httpsCallable('createScenario');
    return callable({
      companyId,
      scenarioName,
      accessToken: this.localStorageService.getAccessToken()
    });
  };

  createScenarioWithPreviousYear = (companyId: string, scenarioName: string, monthlyAccountsForSpan: MonthlyAccounts[]): Observable<Scenario> => {
    const budget = this.createBudgetFromLastYearMonthlyAccounts(monthlyAccountsForSpan);

    const callable = this.functions.httpsCallable('createScenario');
    return callable({
      companyId,
      scenarioName,
      budget,
      accessToken: this.localStorageService.getAccessToken()
    });
  };

  createBudgetFromLastYearMonthlyAccounts = (monthlyAccountsList: MonthlyAccounts[]): any =>
    monthlyAccountsList.reduce((budgetData, monthlyAccounts) => {
      const yearMonthBudgetData = monthlyAccounts.accountsValues.reduce((yearMonthBudgetData, accountValue) => {
        if (Number(accountValue.accountNumber) >= 3000 && accountValue.value !== 0) {
          return {
            ...yearMonthBudgetData,
            [accountValue.accountNumber]: accountValue.value
          }
        } else {
          return yearMonthBudgetData;
        }
      }, {});
      const newYearMonth = incrementYear(monthlyAccounts.yearMonth);
      return {
        ...budgetData,
        [newYearMonth]: yearMonthBudgetData
      };
    }, {});

  createBudgetCsvObjectForPeriod = (monthlyAccountsForSpan: MonthlyAccounts[]): any =>
    monthlyAccountsForSpan.sort((a, b) => a.yearMonth.localeCompare(b.yearMonth))
      .reduce((accountsAcc: any, monthlyAccounts) => {
      return monthlyAccounts.accountsValues.reduce((subAccountsAcc: any, accountValue) => {
        if (Number(accountValue.accountNumber) < 3000) {
          return subAccountsAcc;
        }
        if (!subAccountsAcc[accountValue.accountNumber]) {
          return {
            ...subAccountsAcc,
            [accountValue.accountNumber]: [accountValue.value]
          }
        } else {
          subAccountsAcc[accountValue.accountNumber].push(accountValue.value);
          return subAccountsAcc;
        }
      }, accountsAcc);
    }, {});

  createBudgetCsvRowsForPeriod = (monthlyAccountsForSpan: MonthlyAccounts[], referenceAccounts: Account[]): string[] => {
    const csvObject = this.createBudgetCsvObjectForPeriod(monthlyAccountsForSpan);
    const accounts = Object.keys(csvObject);
    return accounts.map(account => {
      // todo add account label
      const label = referenceAccounts.find(referenceAccount => String(referenceAccount.number) === account)?.name ?? '';
      const values: number[] = csvObject[account];
      return `${account} - ${label}; ${values.map(value => value !== 0 ? value.toString() : '').join(`${CSV_SEPARATOR} `)}`;
    });
  };

  createCsvForPeriod = (monthlyAccountsWithinExistingSpan: MonthlyAccounts[], accounts: Account[]): string => {
    const yearMonthHeader = monthlyAccountsWithinExistingSpan.sort((a, b) => a.yearMonth.localeCompare(b.yearMonth))
      .map(monthlyAccounts => monthlyAccounts.yearMonth)
      .join(`${CSV_SEPARATOR} `);
    const header = `account${CSV_SEPARATOR} ${yearMonthHeader}`;
    const rows = this.createBudgetCsvRowsForPeriod(monthlyAccountsWithinExistingSpan, accounts);
    const fullCsvRows = [header, ...rows];
    return fullCsvRows.join('\n');
  };

  renameScenario = (companyId: string, scenarioId: string, scenarioName: string): Observable<string> => {
    const callable = this.functions.httpsCallable('renameScenario');
    return callable({
      companyId,
      scenarioId,
      scenarioName,
      accessToken: this.localStorageService.getAccessToken(),
    });
  };

  deleteScenario = (companyId: string, scenarioId: string): Observable<void> => {
    const callable = this.functions.httpsCallable('deleteScenario');
    return callable({
      companyId,
      scenarioId,
      accessToken: this.localStorageService.getAccessToken()
    });
  };

  duplicateScenario = (companyId: string, scenarioId: string, scenarioName: string): Observable<Scenario> => {
    const callable = this.functions.httpsCallable('duplicateScenario');
    return callable({
      companyId,
      scenarioId,
      scenarioName,
      accessToken: this.localStorageService.getAccessToken()
    });
  };

}
